import React from "react";
import cn from "classnames";
import cls from "./styles.module.scss";

const CENTER = 200;
const RADIUS = 188;
const LABEL_RADIUS = 126;

const polarToCartesian = (angle: number, radius: number) => {
  const radians = ((angle - 90) * Math.PI) / 180;

  return {
    x: CENTER + radius * Math.cos(radians),
    y: CENTER + radius * Math.sin(radians),
  };
};

const createSectorPath = (startAngle: number, endAngle: number) => {
  const start = polarToCartesian(startAngle, RADIUS);
  const end = polarToCartesian(endAngle, RADIUS);
  const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

  return [
    `M ${CENTER} ${CENTER}`,
    `L ${start.x} ${start.y}`,
    `A ${RADIUS} ${RADIUS} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`,
    "Z",
  ].join(" ");
};

const formatMultiplier = (value: number) => `x${value}`;

interface GoalMultiplierSpinWheelProps {
  rotation: number;
  spinning: boolean;
  selectedMultiplier?: number | null;
  multipliers: number[]
}

export const GoalMultiplierSpinWheel = ({
  rotation,
  spinning,
  selectedMultiplier = null,
  multipliers
}: GoalMultiplierSpinWheelProps) => {
  const sectorAngle = 360 / multipliers.length;

  return (
    <div className={cls.spinWheel} aria-hidden>
      <svg className={cls.spinWheelSvg} viewBox="0 0 400 400">
        <defs>
          <filter id="multiplierSectorInnerShadow">
            <feOffset dx="0" dy="8" />
            <feGaussianBlur stdDeviation="10" result="offsetBlur" />
            <feComposite
              in="offsetBlur"
              in2="SourceAlpha"
              operator="arithmetic"
              k2="-1"
              k3="1"
              result="innerShadow"
            />
            <feColorMatrix
              in="innerShadow"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.48 0"
            />
            <feComposite in2="SourceGraphic" operator="over" />
          </filter>
          <filter
            id="multiplierWinningSectorGlow"
            x="-20%"
            y="-20%"
            width="140%"
            height="140%"
          >
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feColorMatrix
              in="blur"
              type="matrix"
              values="0 0 0 0 0.99 0 0 0 0 0.74 0 0 0 0 0 0 0 0 0.52 0"
            />
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <g
          className={cn(cls.spinWheelSectors, spinning && cls.spinning)}
          style={{ transform: `rotate(${rotation}deg)` }}
        >
          {multipliers.map((value, index) => {
            const startAngle = index * sectorAngle;
            const endAngle = startAngle + sectorAngle;
            const midAngle = startAngle + sectorAngle / 2;
            const labelPoint = polarToCartesian(midAngle, LABEL_RADIUS);

            return (
              <g key={value}>
                <path
                  className={cn(
                    index % 2 === 0
                      ? cls.spinWheelSectorDark
                      : cls.spinWheelSectorLight,
                    selectedMultiplier === value && cls.spinWheelSectorWin
                  )}
                  d={createSectorPath(startAngle, endAngle)}
                  filter={
                    selectedMultiplier === value
                      ? "url(#multiplierWinningSectorGlow)"
                      : "url(#multiplierSectorInnerShadow)"
                  }
                />
                <text
                  className={cls.spinWheelLabel}
                  x={labelPoint.x}
                  y={labelPoint.y}
                  transform={`rotate(${midAngle} ${labelPoint.x} ${labelPoint.y})`}
                >
                  {formatMultiplier(value)}
                </text>
              </g>
            );
          })}
        </g>
        <image
          className={cls.spinWheelPin}
          href="/goals/wheelCenter.webp"
          x="138"
          y="138"
          width="124"
          height="124"
          preserveAspectRatio="xMidYMid meet"
        />
      </svg>
    </div>
  );
};
